import firebase from 'firebase/app';
import 'firebase/auth';
import Cookies from 'js-cookie';
import logger from '../logger';
import authLog from './authDebugLog';
import { DRYNOTES_LOGIN, COOKIE_KEYS } from '../constants';
import { firebaseConfig, validateFirebaseConfig } from '../config/firebase';
import publicEnv from '../publicEnv';
const { DRYNOTES_ENV, API_BASE } = publicEnv;

// Initialize Firebase only if config is valid
let app;
let auth;

try {
  if (validateFirebaseConfig()) {
    app = firebase.initializeApp(firebaseConfig);
    auth = firebase.auth();
    logger.info('Firebase initialized successfully');
  } else {
    logger.warn(
      'Firebase configuration incomplete, authentication will be limited'
    );
  }
} catch (error) {
  logger.error('Failed to initialize Firebase:', error);
}

/**
 * Exchange the current Firebase ID token for a fresh dry_jwt session cookie.
 * Used by dryFetch to silently recover from a 401 without redirecting to login.
 * Calls GET /sessionToken with the Firebase `access` auth strategy, so it works
 * even when the existing dry_jwt is expired.
 */
export const refreshSessionViaFirebase = async () => {
  authLog('refreshSessionViaFirebase:start');
  if (!auth || !auth.currentUser) {
    logger.warn('refreshSessionViaFirebase: no Firebase user — cannot refresh session');
    authLog('refreshSessionViaFirebase:no-user');
    return null;
  }

  let idToken;
  try {
    idToken = await auth.currentUser.getIdToken(true); // force-refresh
  } catch (err) {
    logger.warn('refreshSessionViaFirebase: failed to get Firebase ID token:', err);
    authLog('refreshSessionViaFirebase:token-error', { error: String(err) });
    return null;
  }

  try {
    const response = await fetch(`${API_BASE}/sessionToken`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      logger.warn('refreshSessionViaFirebase: /sessionToken returned', response.status);
      authLog('refreshSessionViaFirebase:http-error', { status: response.status });
      return null;
    }

    const data = await response.json();
    if (data.status !== 'success' || !data.jwt) {
      logger.warn('refreshSessionViaFirebase: unexpected /sessionToken response', data.status);
      authLog('refreshSessionViaFirebase:bad-response', { status: data.status });
      return null;
    }

    const domain = DRYNOTES_ENV === 'development' ? '.drynotes.cc' : '.drycloud.app';
    const ONE_HOUR_MS = 3600 * 1000;
    const effectiveMaxAge = Math.max(data.options?.maxAge ?? ONE_HOUR_MS, ONE_HOUR_MS);
    const expireTime = new Date(Date.now() + effectiveMaxAge);

    Cookies.set(COOKIE_KEYS.jwt, data.jwt, {
      expires: expireTime,
      domain,
      secure: true,
      sameSite: 'strict',
    });

    logger.info('refreshSessionViaFirebase: session cookie refreshed successfully');
    authLog('refreshSessionViaFirebase:success', { expiresAt: expireTime.toISOString() });
    return data.jwt;
  } catch (err) {
    logger.error('refreshSessionViaFirebase: error calling /sessionToken:', err);
    authLog('refreshSessionViaFirebase:error', { error: String(err) });
    return null;
  }
};

/**
 * Validates if the current user is authenticated with Firebase
 * This checks both the Firebase auth state and validates the JWT token
 */
export const validateFirebaseAuth = async () => {
  authLog('validateFirebaseAuth:start');
  if (!auth) {
    logger.warn(
      'Firebase auth not initialized, falling back to cookie check'
    );
    // Fallback to basic cookie check if Firebase is not available
    const ownerID = Cookies.get(COOKIE_KEYS.userID);
    const jwt = Cookies.get(COOKIE_KEYS.jwt);
    const result = !!(ownerID && jwt);
    authLog('validateFirebaseAuth:no-firebase-fallback', { ownerID: !!ownerID, jwt: !!jwt, result });
    return result;
  }

  // Wait for Firebase to restore its IndexedDB session before concluding
  // there is no user.  auth.currentUser is synchronously null during the
  // async restore window; checking it directly causes a race: the heartbeat
  // sees null, returns false, retries 3×, and forces logout even though the
  // user is authenticated.  onAuthStateChanged fires once the restore settles.
  const RESTORE_TIMEOUT_MS = 3000;
  let settled = false;
  const currentUser = await new Promise((res) => {
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      res(auth.currentUser); // fall back to synchronous value after timeout
    }, RESTORE_TIMEOUT_MS);

    const unsub = auth.onAuthStateChanged((user) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsub();
      res(user);
    });
  });

  if (!currentUser) {
    // Firebase has no local session on this origin. This happens because login
    // occurs at drycloud.app/login (different origin) — Firebase IndexedDB is
    // scoped per origin and does not carry across subdomains.
    //
    // Bootstrap the session: exchange the valid dry_jwt session cookie for a
    // Firebase custom token via /auth/firebase-token, then call
    // signInWithCustomToken() to establish a real local Firebase session.
    // After this, the heartbeat and renewJWT work normally.
    const ownerID = Cookies.get(COOKIE_KEYS.userID);
    const jwt = Cookies.get(COOKIE_KEYS.jwt);

    if (ownerID && jwt) {
      try {
        logger.info('validateFirebaseAuth: no local Firebase session — bootstrapping via custom token');
        authLog('validateFirebaseAuth:bootstrap-start');
        // Dynamic import avoids circular dep: firebaseAuth → dryFetch → firebaseAuth
        const { dryFetch } = await import('./dryFetch');
        const resp = await dryFetch({
          name: 'getFirebaseCustomToken',
          path: '/auth/firebase-token',
          options: { method: 'POST' },
        });
        if (resp && resp.customToken) {
          const userCredential = await auth.signInWithCustomToken(resp.customToken);
          if (userCredential && userCredential.user) {
            const bootstrappedUser = userCredential.user;
            logger.info('validateFirebaseAuth: Firebase session bootstrapped', { uid: bootstrappedUser.uid });
            authLog('validateFirebaseAuth:bootstrap-success', { uid: bootstrappedUser.uid });
            // Get a token to confirm the session is usable
            const token = await bootstrappedUser.getIdToken();
            if (token) {
              authLog('validateFirebaseAuth:success-after-bootstrap');
              return true;
            }
          }
        }
        throw new Error('Custom token response missing or signIn returned no user');
      } catch (err) {
        // Bootstrap failed — fall back to cookie presence so the user is not
        // logged out while the session cookie is still server-side valid.
        logger.error('validateFirebaseAuth: custom token bootstrap failed, falling back to cookie validation:', err);
        authLog('validateFirebaseAuth:bootstrap-failed-cookie-fallback', { error: String(err) });
        return true;
      }
    }

    logger.info('No Firebase session and no cookies — not authenticated');
    authLog('validateFirebaseAuth:no-user', { uid: null });
    return false;
  }

  authLog('validateFirebaseAuth:user-found', { uid: currentUser.uid });

  // Only force-refresh if the token expires within 5 minutes
  const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;
  try {
    const tokenResult = await currentUser.getIdTokenResult();
    const expiresAt = new Date(tokenResult.expirationTime).getTime();
    const expiresInMs = expiresAt - Date.now();
    const needsRefresh = expiresInMs < REFRESH_THRESHOLD_MS;
    authLog('validateFirebaseAuth:token-check', {
      expiresAt: tokenResult.expirationTime,
      expiresInMin: Math.round(expiresInMs / 60000),
      needsRefresh,
    });
    if (needsRefresh) {
      logger.debug('Firebase token expiring soon, forcing refresh');
    }
    const token = await currentUser.getIdToken(needsRefresh);
    if (token) {
      logger.info('Firebase JWT token validated successfully');
      authLog('validateFirebaseAuth:success');
      return true;
    }
    logger.warn('No valid JWT token found');
    authLog('validateFirebaseAuth:no-token');
    return false;
  } catch (error) {
    logger.error('Failed to get Firebase JWT token:', error);
    authLog('validateFirebaseAuth:error', { error: String(error) });
    return false;
  }
};

/**
 * Sets up Firebase auth state listener
 * This will automatically handle auth state changes
 */
export const setupFirebaseAuthListener = (onAuthChange) => {
  if (!auth) {
    logger.warn('Firebase auth not initialized, auth listener not available');
    return () => {}; // Return no-op unsubscribe function
  }

  return auth.onAuthStateChanged((user) => {
    if (user) {
      logger.info('Firebase user authenticated:', user.uid);
      onAuthChange(true, user);
    } else {
      logger.info('Firebase user signed out');
      onAuthChange(false, null);
    }
  });
};

/**
 * Signs out the current user and redirects to login.
 *
 * Sequence:
 *   1. POST /logout — revokes Firebase refresh tokens server-side so the
 *      session cannot be silently renewed and ID tokens cannot be refreshed.
 *   2. Firebase SDK signOut() — clears the local IndexedDB session.
 *   3. Remove dry_jwt and dry_token cookies.
 *   4. Redirect to login.
 *
 * Step 1 is best-effort: if the API call fails (e.g. network down) we still
 * proceed with local cleanup and redirect so the user is not stuck.
 */
export const signOutAndRedirect = async () => {
  // Capture caller stack so we know what triggered the logout
  const callerStack = new Error().stack || '';
  authLog('signOutAndRedirect:called', { stack: callerStack.split('\n').slice(1, 6).join(' | ') });
  // Step 1: server-side revocation (best-effort)
  try {
    // Dynamic import avoids circular dep: firebaseAuth → dryFetch → firebaseAuth
    const { dryFetch } = await import('./dryFetch');
    await dryFetch({
      name: 'logout',
      path: '/logout',
      options: { method: 'POST' },
    });
    logger.info('signOutAndRedirect: server-side tokens revoked');
  } catch (error) {
    logger.warn('signOutAndRedirect: server-side revocation failed (non-fatal), proceeding with local logout:', error);
  }

  // Step 2: Firebase SDK sign-out
  try {
    if (auth && auth.currentUser) {
      await auth.signOut();
      logger.info('User signed out successfully');
    }
  } catch (error) {
    logger.error('Error signing out of Firebase SDK:', error);
  }

  // Step 3: clear cookies
  const cookieDomain =
    DRYNOTES_ENV === 'development' ? '.drynotes.cc' : '.drycloud.app';
  Cookies.remove(COOKIE_KEYS.userID, { domain: cookieDomain });
  Cookies.remove(COOKIE_KEYS.jwt, { domain: cookieDomain });

  // Step 4: redirect
  logger.info('Redirecting to login application:', DRYNOTES_LOGIN);
  authLog('signOutAndRedirect:redirecting', { target: DRYNOTES_LOGIN });
  window.location.href = DRYNOTES_LOGIN;
};

/**
 * Gets the current Firebase user
 */
export const getCurrentFirebaseUser = () => {
  return auth ? auth.currentUser : null;
};

/**
 * Checks if Firebase is properly initialized
 */
export const isFirebaseInitialized = () => {
  return !!auth;
};

/**
 * Waits for Firebase's own session restore via onAuthStateChanged.
 * This is the correct way to check auth on initial page load — Firebase
 * restores the session from IndexedDB persistence before the first emission.
 * Falls back to cookie check if Firebase is not initialized or times out.
 */
export const waitForAuthState = () => {
  return new Promise((resolve) => {
    if (!auth) {
      logger.warn('Firebase not initialized, falling back to cookie check');
      const ownerID = Cookies.get(COOKIE_KEYS.userID);
      const jwt = Cookies.get(COOKIE_KEYS.jwt);
      resolve(!!(ownerID && jwt));
      return;
    }

    const TIMEOUT_MS = 5000;
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      logger.warn('waitForAuthState: timed out waiting for Firebase, falling back to cookie check');
      const ownerID = Cookies.get(COOKIE_KEYS.userID);
      const jwt = Cookies.get(COOKIE_KEYS.jwt);
      resolve(!!(ownerID && jwt));
    }, TIMEOUT_MS);

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe();
      if (user) {
        logger.info('waitForAuthState: session restored for', user.uid);
        resolve(true);
      } else {
        logger.info('waitForAuthState: no active session');
        resolve(false);
      }
    });
  });
};

/**
 * Authentication heartbeat process
 * Checks authentication validity every 2 minutes
 * If invalid, attempts to refresh token
 * If refresh fails, redirects to login page
 * Accounts for different environments (development vs production)
 */
let authHeartbeatTimeout = null;
let isHeartbeatRunning = false;
let authRetryCount = 0;
const AUTH_MAX_RETRIES = 3;
const AUTH_HEARTBEAT_INTERVAL = 600000; // 10 minutes
const AUTH_RETRY_BASE_DELAY = 30000; // 30 seconds

export const startAuthHeartbeat = () => {
  // Prevent multiple heartbeat instances
  if (isHeartbeatRunning) {
    logger.debug('Auth heartbeat already running, skipping start');
    return;
  }

  // Clear any existing heartbeat
  if (authHeartbeatTimeout) {
    clearTimeout(authHeartbeatTimeout);
    authHeartbeatTimeout = null;
  }

  isHeartbeatRunning = true;
  authRetryCount = 0;
  logger.info('Starting authentication heartbeat (10 minute interval)');

  const checkAuth = async () => {
    authLog('heartbeat:check', { retry: authRetryCount });
    try {
      logger.debug('Auth heartbeat: Checking authentication validity');
      const isValid = await validateFirebaseAuth();

      if (isValid) {
        logger.debug('Auth heartbeat: Authentication is valid');
        authLog('heartbeat:valid');
        authRetryCount = 0;
        // Renew the dry_jwt cookie alongside the Firebase token.
        // Dynamic import avoids a circular dep: firebaseAuth→dryAuth→dryFetch→firebaseAuth
        import('./dryAuth').then(({ renewJWT }) =>
          renewJWT().catch((err) => {
            logger.warn('Auth heartbeat: JWT renewal failed (non-fatal):', err);
            authLog('heartbeat:renewJWT-failed', { error: String(err) });
          })
        );
        authHeartbeatTimeout = setTimeout(checkAuth, AUTH_HEARTBEAT_INTERVAL);
      } else {
        authRetryCount++;
        authLog('heartbeat:invalid', { retry: authRetryCount, maxRetries: AUTH_MAX_RETRIES });
        if (authRetryCount <= AUTH_MAX_RETRIES) {
          const delay = AUTH_RETRY_BASE_DELAY * authRetryCount;
          logger.warn(
            `Auth heartbeat: Auth invalid, retry ${authRetryCount}/${AUTH_MAX_RETRIES} in ${delay / 1000}s`
          );
          authHeartbeatTimeout = setTimeout(checkAuth, delay);
        } else {
          logger.warn('Auth heartbeat: Auth invalid after max retries, redirecting to login');
          authLog('heartbeat:max-retries-exceeded-logout');
          isHeartbeatRunning = false;
          await signOutAndRedirect();
        }
      }
    } catch (error) {
      authRetryCount++;
      authLog('heartbeat:error', { retry: authRetryCount, error: String(error) });
      if (authRetryCount <= AUTH_MAX_RETRIES) {
        const delay = AUTH_RETRY_BASE_DELAY * authRetryCount;
        logger.error(
          `Auth heartbeat: Error on check, retry ${authRetryCount}/${AUTH_MAX_RETRIES} in ${delay / 1000}s:`,
          error
        );
        authHeartbeatTimeout = setTimeout(checkAuth, delay);
      } else {
        logger.error('Auth heartbeat: Error after max retries, redirecting to login:', error);
        authLog('heartbeat:max-retries-error-logout', { error: String(error) });
        isHeartbeatRunning = false;
        await signOutAndRedirect();
      }
    }
  };

  // Start the first check after 10 minutes
  authHeartbeatTimeout = setTimeout(checkAuth, AUTH_HEARTBEAT_INTERVAL);
};

/**
 * Stops the authentication heartbeat process
 */
export const stopAuthHeartbeat = () => {
  if (authHeartbeatTimeout) {
    clearTimeout(authHeartbeatTimeout);
    authHeartbeatTimeout = null;
    isHeartbeatRunning = false;
    logger.info('Authentication heartbeat stopped');
  }
};

/**
 * Registers a visibilitychange listener that calls renewJWT() whenever the
 * user returns to the tab.  This ensures sessions are extended on actual
 * activity, not only on the 10-minute heartbeat tick.
 *
 * Works alongside the heartbeat — both call the same renewJWT() which POSTs
 * to /renewSession.  The server-side threshold (10 days) ensures this is a
 * no-op unless a renewal is actually due, so calling it on every focus event
 * is safe and cheap.
 *
 * @returns {Function} Unsubscribe function — call it to remove the listener
 *   (e.g. when the user logs out or the component unmounts).
 */
export const startFocusRenewal = () => {
  function onVisibilityChange() {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      logger.debug('Focus renewal: tab visible, triggering renewJWT');
      // Dynamic import avoids the same circular dep as the heartbeat path
      import('./dryAuth').then(({ renewJWT }) =>
        renewJWT().catch((err) =>
          logger.warn('Focus renewal: renewJWT failed (non-fatal):', err)
        )
      );
    }
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
    logger.info('Focus renewal listener registered');
  }

  return () => {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      logger.info('Focus renewal listener removed');
    }
  };
};

export default {
  refreshSessionViaFirebase,
  validateFirebaseAuth,
  waitForAuthState,
  setupFirebaseAuthListener,
  signOutAndRedirect,
  getCurrentFirebaseUser,
  isFirebaseInitialized,
  startAuthHeartbeat,
  stopAuthHeartbeat,
  startFocusRenewal,
};
